import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useGuardStore } from "../context/GuardContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 5;

export default function GuardNotification() {
  const { selectedGuard } = useGuardStore();
  const guardId = selectedGuard?._id || selectedGuard?.id;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  /* 🔊 SOUND */
  const notificationSound = useRef(new Audio("/notify.mp3"));
  const prevUnreadCount = useRef(0);

  /* ---------------- FETCH NOTIFICATIONS (MANUAL) ---------------- */
  const fetchNotifications = async () => {
    if (!guardId) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("guardToken");

      const res = await axios.get(
        `${BASE_URL}/api/notification/${guardId}/guard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sortedData = Array.isArray(res.data)
        ? [...res.data].reverse()
        : [];

      setNotifications(sortedData);
    } catch (err) {
      console.error("Notification fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- FETCH ON GUARD CHANGE ---------------- */
  useEffect(() => {
    if (!guardId) return;
    setNotifications([]);
    setCurrentPage(1);
    fetchNotifications();
  }, [guardId]);

  /* ---------------- UNREAD COUNT ---------------- */
  const unreadCount = notifications.filter(
    (n) => !n.notificationStatus
  ).length;

  /* 🔊 SOUND ON NEW NOTIFICATION */
  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      notificationSound.current.play().catch(() => {});
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  /* ---------------- SEARCH + FILTER ---------------- */
  const filteredData = useMemo(() => {
    const text = search.trim().toLowerCase();

    return notifications.filter((n) => {
      const searchMatch = text
        ? [
            n.notificationMessage,
            n.officer?.notificationSenderName,
            n.notificationStatus ? "read" : "unread",
          ].some((field) =>
            String(field || "").toLowerCase().includes(text)
          )
        : true;

      const statusMatch = statusFilter
        ? statusFilter === "read"
          ? n.notificationStatus
          : !n.notificationStatus
        : true;

      return searchMatch && statusMatch;
    });
  }, [notifications, search, statusFilter]);

  /* ---------------- PAGINATION ---------------- */
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ---------------- DATE FORMAT ---------------- */
  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    return isNaN(d)
      ? "--"
      : d.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  /* ---------------- MARK SINGLE READ ---------------- */
  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("guardToken");

      await axios.get(
        `${BASE_URL}/api/notification/read/guard/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notificationSound.current.play().catch(() => {});
      fetchNotifications();
    } catch (err) {
      console.error("Mark read failed", err);
    }
  };

  /* ---------------- MARK ALL READ ---------------- */
  const handleMarkAllAsRead = async () => {
    const token = localStorage.getItem("guardToken");
    const unread = notifications.filter(
      (n) => !n.notificationStatus
    );

    try {
      await Promise.all(
        unread.map((n) =>
          axios.get(
            `${BASE_URL}/api/notification/read/guard/${n.notificationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      notificationSound.current.play().catch(() => {});
      fetchNotifications();
    } catch (err) {
      console.error("Mark all read failed", err);
    }
  };

  /* ---------------- UI ---------------- */
  if (!guardId) {
    return (
      <div style={styles.container}>
        <div style={styles.noData}>No Officer Selected</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>
        Officer Notification Management ({unreadCount})
      </h2>

      {/* FILTERS */}
      <div style={styles.filterRow}>
        <div style={styles.filterCard}>
          <label style={styles.filterLabel}>Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notification..."
            style={styles.input}
          />
        </div>

        <div style={styles.filterCard}>
          <label style={styles.filterLabel}>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="">All</option>
            <option value="read">Read</option>
            <option value="unread">Unread</option>
          </select>
        </div>

        <div style={{ alignSelf: "end", display: "flex", gap: 10 }}>
          <button style={styles.refreshBtn} onClick={fetchNotifications}>
            Refresh
          </button>
          <button style={styles.acceptBtn} onClick={handleMarkAllAsRead}>
            Mark All Read
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.noData}>Loading...</div>
        ) : paginatedData.length === 0 ? (
          <div style={styles.noData}>No Notifications Found</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Message</th>
                <th style={styles.th}>Receive Time</th>
                <th style={styles.th}>Read Time</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.notificationId}>
                  <td style={styles.td}>
                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                  </td>
                  <td style={styles.td}>{item.notificationMessage}</td>
                  <td style={styles.td}>
                    {formatDate(item.notificationAssignTime)}
                  </td>
                  <td style={styles.td}>
                    {formatDate(item.notificationReadTime)}
                  </td>
                  <td style={styles.td}>
                    {item.notificationStatus ? (
                      <span style={{ color: "#888", fontWeight: 600 }}>
                        Completed
                      </span>
                    ) : (
                      <button
                        style={styles.acceptBtn}
                        onClick={() =>
                          handleMarkAsRead(item.notificationId)
                        }
                      >
                        Mark Read
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- STYLES ---------------- */
const styles = {
  container: { padding: 30, background: "#fff" },
  title: { fontSize: 30, fontWeight: 800, marginBottom: 25, color: "#1967d2" },
  filterRow: { display: "flex", gap: 20, marginBottom: 25 },
  filterCard: {
    flex: 1,
    padding: 18,
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
  },
  filterLabel: { fontSize: 15, fontWeight: 600 },
  select: {
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "1.5px solid #1a73e8",
  },
  input: {
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "1.5px solid #1a73e8",
  },
  card: {
    background: "#fff",
    padding: 10,
    borderRadius: 15,
    boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    background: "#f4f4f6",
    padding: 16,
    borderBottom: "2px solid #ddd",
  },
  td: { padding: 14, borderBottom: "1px solid #eee" },
  acceptBtn: {
    background: "#2e7d32",
    color: "#fff",
    border: "none",
    padding: "7px 14px",
    borderRadius: 8,
    cursor: "pointer",
  },
  refreshBtn: {
    background: "#1a73e8",
    color: "#fff",
    border: "none",
    padding: "7px 14px",
    borderRadius: 8,
    cursor: "pointer",
  },
  noData: {
    textAlign: "center",
    padding: 40,
    fontSize: 18,
    color: "#777",
  },
};
